/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import React, { useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { DateInput } from '../../inputs/form/DateInput/DateInput';
import { SelectInput } from '../../inputs/form/SelectInput/SelectInput';
import { BaseFilter } from '../BaseFilter';
import { CUSTOM_DATE_RANGE_PRESET_ID, DEFAULT_PRESETS } from './presets';
import './DateRangeFilterStyles.scss';
function formatRange(value) {
    if (!value.start && !value.end)
        return undefined;
    const start = value.start ? dayjs(value.start).format('MMM D, YYYY') : 'Not set';
    const end = value.end ? dayjs(value.end).format('MMM D, YYYY') : 'Not set';
    return `${start} - ${end}`;
}
/** Keep start ≤ end when both bounds are set. Independent picker edits can reverse the range. */
export function orderDateRange(range) {
    const { start, end } = range;
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
        return { start: end, end: start };
    }
    return range;
}
export function DateRangeFilter({ label, value, onChange, presets = DEFAULT_PRESETS, resetOnCustomRange = false, selectedPresetId, onPresetSelect, presetGroupLabel = 'Date presets', disabled = false, showReset = true }) {
    var _a, _b;
    // React 17-compatible stable ID using useRef with lazy initialization (no useId() until
    // React 18). FilterBar renders one DateRangeFilter per configured dateRange field —
    // hardcoded "date-range-start"/"date-range-end" ids collided across instances and DateInput's
    // label htmlFor bound to the wrong input whenever two were active at once.
    const uniqueIdRef = useRef();
    if (!uniqueIdRef.current) {
        uniqueIdRef.current = `date-range-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    const uniqueId = uniqueIdRef.current;
    const isActive = value.start != null || value.end != null;
    const canReset = isActive || (selectedPresetId != null && selectedPresetId !== CUSTOM_DATE_RANGE_PRESET_ID);
    const selectedDisplay = useMemo(() => formatRange(value), [value]);
    const popupSettings = useMemo(() => ({
        appendTo: typeof document === 'undefined' ? undefined : document.body,
        popupClass: 'qmb-ui-date-range-filter__picker-popup'
    }), []);
    const customPreset = presets.find((preset) => preset.id === CUSTOM_DATE_RANGE_PRESET_ID);
    const presetOptions = useMemo(() => presets.map((preset) => ({ id: preset.id, value: preset.id, label: preset.label })), [presets]);
    const selectedValue = (_a = selectedPresetId !== null && selectedPresetId !== void 0 ? selectedPresetId : customPreset === null || customPreset === void 0 ? void 0 : customPreset.id) !== null && _a !== void 0 ? _a : (_b = presets[0]) === null || _b === void 0 ? void 0 : _b.id;
    const handleReset = useCallback(() => {
        onChange({ start: null, end: null });
    }, [onChange]);
    const handleBoundChange = useCallback((bound, nextValue) => {
        onPresetSelect === null || onPresetSelect === void 0 ? void 0 : onPresetSelect(CUSTOM_DATE_RANGE_PRESET_ID);
        onChange(orderDateRange({
            start: bound === 'start' ? nextValue : value.start,
            end: bound === 'end' ? nextValue : value.end
        }));
    }, [onChange, onPresetSelect, value.end, value.start]);
    const handlePresetChange = useCallback((presetId) => {
        const preset = presets.find((candidate) => candidate.id === presetId);
        if (!preset)
            return;
        if (preset.id === CUSTOM_DATE_RANGE_PRESET_ID) {
            onPresetSelect === null || onPresetSelect === void 0 ? void 0 : onPresetSelect(CUSTOM_DATE_RANGE_PRESET_ID);
            if (resetOnCustomRange && !onPresetSelect) {
                onChange({ start: null, end: null });
            }
            return;
        }
        if (onPresetSelect) {
            onPresetSelect(preset.id);
        }
        else {
            onChange(preset.compute());
        }
    }, [onChange, onPresetSelect, presets, resetOnCustomRange]);
    return (React.createElement(BaseFilter, { label: label, isActive: isActive, selectedDisplay: selectedDisplay, disabled: disabled, onReset: showReset && canReset ? handleReset : undefined }, () => (React.createElement("div", { className: "qmb-ui-date-range-filter" },
        React.createElement(SelectInput, { id: `${uniqueId}-preset`, label: "Date Range", options: presetOptions, value: selectedValue, onChange: handlePresetChange, disabled: disabled }),
        React.createElement(DateInput, { id: `${uniqueId}-start`, name: "start", type: "date", label: "Start", value: value.start, popupSettings: popupSettings, onChange: (nextValue) => handleBoundChange('start', nextValue) }),
        React.createElement(DateInput, { id: `${uniqueId}-end`, name: "end", type: "date", label: "End", value: value.end, popupSettings: popupSettings, onChange: (nextValue) => handleBoundChange('end', nextValue) }),
        React.createElement("span", { className: "qmb-ui-date-range-filter__presets-sr", role: "group", "aria-label": presetGroupLabel })))));
}
